/* Phase 4 panels — Health Score, Operations (BC/DR + Vendor reviews + Access reviews),
   Policy Lifecycle, Training. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, HeartPulse, LifeBuoy, Building2, BookOpenCheck, GraduationCap, UserCheck } from "lucide-react";

const inputCls = "rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30";
const err = (e) => formatApiErrorDetail(e.response?.data?.detail?.message || e.response?.data?.detail) || "Failed";
const Spinner = () => <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
const PRI_TONE = { critical: "bg-red-500 text-white", high: "bg-red-50 text-red-500", medium: "bg-amber-50 text-amber-600", low: "bg-slate-100 text-slate-500" };

export const HealthPanel = ({ onNavigate }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/compliance/health").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Spinner />;
  const S = d.sections;
  return (
    <div className="space-y-4" data-testid="cc-health">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="rounded-2xl bg-navy text-white p-4" data-testid="health-overall">
          <div className="text-[10px] font-bold uppercase text-white/50">Compliance Health</div>
          <div className="font-heading text-2xl font-extrabold">{d.overall_health_pct}%</div>
        </div>
        {[["Technical Controls", S.technical_controls], ["Administrative Controls", S.administrative_controls],
          ["Physical Controls", S.physical_controls], ["Documentation", S.documentation]].map(([label, s]) => (
          <div key={label} className="rounded-2xl bg-white border border-slate-100 p-4" data-testid={`health-${label.split(" ")[0].toLowerCase()}`}>
            <div className="text-[10px] font-bold uppercase text-slate-400 leading-tight">{label}</div>
            <div className="font-heading text-2xl font-extrabold text-navy">{s.score}%</div>
            <div className="text-[10px] text-slate-400">{s.passing ?? s.published ?? 0}/{s.total}</div>
          </div>
        ))}
      </div>
      <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="health-summary">
        <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><HeartPulse className="w-4 h-4 text-cobalt" /> Executive summary</h3>
        <p className="text-xs text-slate-600 mt-2">{d.executive_summary}</p>
      </div>
      <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="health-recs">
        <h3 className="font-heading font-bold text-navy text-sm">Prioritized remediation recommendations</h3>
        <div className="mt-3 space-y-1.5">
          {d.remediation_recommendations.map((r, i) => (
            <div key={i} className="flex items-start gap-2 rounded-xl border border-slate-100 px-3 py-2 text-xs" data-testid={`health-rec-${i}`}>
              <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase whitespace-nowrap ${PRI_TONE[r.priority]}`}>{r.priority}</span>
              <div className="flex-1"><b className="text-navy">{r.area}:</b> <span className="text-slate-600">{r.action}</span>
                <div className="text-slate-400 mt-0.5">{r.impact}</div></div>
              {r.fix_tab && onNavigate && (
                <button onClick={() => onNavigate(r.fix_tab)} data-testid={`health-fix-${i}`}
                  className="px-3 py-1.5 rounded-full bg-cobalt text-white text-[10px] font-bold whitespace-nowrap hover:bg-cobalt/90 transition-colors">Fix this →</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const OperationsPanel = () => {
  const [bcdr, setBcdr] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [vr, setVr] = useState({ vendor_id: "", criticality: "medium", risk_rating: "low", questionnaire_status: "returned", findings: "" });
  const [ar, setAr] = useState({ scope: "all privileged accounts", accounts_reviewed: "", revocations: "0", findings: "" });
  const load = useCallback(() => {
    api.get("/compliance/bcdr").then(({ data }) => setBcdr(data)).catch(() => {});
    api.get("/compliance/vendors/records").then(({ data }) => setVendors(data)).catch(() => {});
    api.get("/compliance/access-reviews").then(({ data }) => setReviews(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!bcdr) return <Spinner />;
  const cfg = bcdr.config;
  const post = async (path, body, ok) => {
    try { await api.post(path, body); toast.success(ok); load(); } catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="space-y-4" data-testid="cc-operations">
      {/* BC/DR */}
      <div className="rounded-3xl bg-white border border-slate-100 p-5">
        <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><LifeBuoy className="w-4 h-4 text-cobalt" /> Business continuity &amp; disaster recovery</h3>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {[["RTO (hours)", "rto_hours"], ["RPO (hours)", "rpo_hours"], ["DR test frequency (days)", "dr_test_frequency_days"]].map(([label, k]) => (
            <div key={k} className="rounded-xl bg-slate-50 p-3">
              <div className="text-[10px] uppercase font-bold text-slate-400">{label}</div>
              <input type="number" defaultValue={cfg[k]} onBlur={async (e) => { const v = Number(e.target.value); if (v && v !== cfg[k]) { await api.put("/compliance/bcdr", { [k]: v }); toast.success("BC/DR config updated"); load(); } }}
                data-testid={`bcdr-${k}`} className={`${inputCls} mt-1 w-full`} />
            </div>
          ))}
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-[10px] uppercase font-bold text-slate-400">Next DR test due</div>
            <div className="font-semibold text-navy mt-2" data-testid="bcdr-next-due">{cfg.next_dr_test_due?.slice(0, 10) || "—"}</div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => post("/compliance/bcdr/backup-verification", { result: "verified", method: "managed database backup restore check" }, "Backup verification recorded — evidence generated (A1.2)")}
            data-testid="bcdr-backup" className="px-4 py-2 rounded-full bg-navy text-white text-xs font-bold">Record backup verification</button>
          <button onClick={() => { const scenario = window.prompt("DR test scenario:", "Full database restore exercise"); if (scenario) post("/compliance/bcdr/dr-test", { scenario, result: "successful", rto_met: true, rpo_met: true }, "DR test recorded — evidence generated"); }}
            data-testid="bcdr-drtest" className="px-4 py-2 rounded-full bg-cobalt text-white text-xs font-bold">Record DR test</button>
        </div>
        <div className="mt-3 grid md:grid-cols-2 gap-3 text-[11px]">
          <div><b className="text-navy text-xs">Backup verification history ({bcdr.backup_verifications.length})</b>
            <div className="mt-1 space-y-1 max-h-36 overflow-y-auto">
              {bcdr.backup_verifications.map((b) => <div key={b.check_id} className="rounded-lg bg-slate-50 px-3 py-1.5 text-slate-500">{b.at.slice(0, 10)} · {b.result} · {b.verified_by}</div>)}
              {!bcdr.backup_verifications.length && <p className="text-slate-300">None recorded — A1.2 stays honest until verified.</p>}
            </div></div>
          <div><b className="text-navy text-xs">DR exercise reports ({bcdr.dr_tests.length})</b>
            <div className="mt-1 space-y-1 max-h-36 overflow-y-auto">
              {bcdr.dr_tests.map((t) => <div key={t.test_id} className="rounded-lg bg-slate-50 px-3 py-1.5 text-slate-500">{t.at.slice(0, 10)} · {t.scenario} · {t.result} · RTO met: {String(t.rto_met)}</div>)}
              {!bcdr.dr_tests.length && <p className="text-slate-300">No DR tests yet.</p>}
            </div></div>
        </div>
      </div>

      {/* vendor reviews */}
      <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="cc-vendor-reviews">
        <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><Building2 className="w-4 h-4 text-cobalt" /> Vendor security reviews</h3>
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <select value={vr.vendor_id} onChange={(e) => setVr({ ...vr, vendor_id: e.target.value })} data-testid="vr-vendor" className={inputCls}>
            <option value="">Select vendor…</option>
            {vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor}</option>)}
          </select>
          <select value={vr.criticality} onChange={(e) => setVr({ ...vr, criticality: e.target.value })} data-testid="vr-criticality" className={inputCls}>
            {["critical", "high", "medium", "low"].map((c) => <option key={c} value={c}>criticality: {c}</option>)}
          </select>
          <select value={vr.risk_rating} onChange={(e) => setVr({ ...vr, risk_rating: e.target.value })} data-testid="vr-rating" className={inputCls}>
            {["low", "medium", "high"].map((c) => <option key={c} value={c}>risk: {c}</option>)}
          </select>
          <select value={vr.questionnaire_status} onChange={(e) => setVr({ ...vr, questionnaire_status: e.target.value })} data-testid="vr-questionnaire" className={inputCls}>
            {["not_sent", "sent", "returned", "reviewed"].map((c) => <option key={c} value={c}>questionnaire: {c}</option>)}
          </select>
          <input placeholder="Findings" value={vr.findings} onChange={(e) => setVr({ ...vr, findings: e.target.value })} data-testid="vr-findings" className={inputCls} />
          <button onClick={() => { if (!vr.vendor_id) { toast.error("Select a vendor"); return; } post(`/compliance/vendors/${vr.vendor_id}/review`, vr, "Vendor review recorded — CC9.2 evidence generated; next review +365d"); }}
            data-testid="vr-submit" className="px-4 py-2 rounded-full bg-navy text-white text-xs font-bold">Record review</button>
        </div>
        <div className="mt-3 space-y-1.5 max-h-56 overflow-y-auto">
          {vendors.map((v) => (
            <div key={v.vendor_id} className="flex flex-wrap justify-between items-center gap-2 rounded-xl border border-slate-100 px-3 py-2 text-xs" data-testid={`vr-row-${v.vendor_id}`}>
              <span className="text-slate-600"><b className="text-navy">{v.vendor}</b> · criticality: {v.criticality || "unset"} · risk: {v.risk_rating}</span>
              <span className="text-slate-400">last review: {v.security_review?.last?.slice(0, 10) || "never"} · renewal due: {v.annual_review_due?.slice(0, 10) || "—"} · insurance: {v.cyber_insurance ? "on file" : "—"}</span>
            </div>
          ))}
        </div>
      </div>

      {/* access reviews */}
      <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="cc-access-reviews">
        <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><UserCheck className="w-4 h-4 text-cobalt" /> Quarterly access reviews (CC6.3)</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          <input placeholder="Scope" value={ar.scope} onChange={(e) => setAr({ ...ar, scope: e.target.value })} data-testid="ar-scope" className={inputCls} />
          <input type="number" placeholder="Accounts reviewed" value={ar.accounts_reviewed} onChange={(e) => setAr({ ...ar, accounts_reviewed: e.target.value })} data-testid="ar-accounts" className={`${inputCls} w-40`} />
          <input type="number" placeholder="Revocations" value={ar.revocations} onChange={(e) => setAr({ ...ar, revocations: e.target.value })} data-testid="ar-revocations" className={`${inputCls} w-32`} />
          <input placeholder="Findings" value={ar.findings} onChange={(e) => setAr({ ...ar, findings: e.target.value })} data-testid="ar-findings" className={inputCls} />
          <button onClick={() => { if (!ar.accounts_reviewed) { toast.error("Enter accounts reviewed"); return; } post("/compliance/access-reviews", { ...ar, accounts_reviewed: Number(ar.accounts_reviewed), revocations: Number(ar.revocations) }, "Access review recorded — CC6.3 evidenced"); }}
            data-testid="ar-submit" className="px-4 py-2 rounded-full bg-navy text-white text-xs font-bold">Record review</button>
        </div>
        <div className="mt-3 space-y-1.5 max-h-40 overflow-y-auto">
          {reviews.map((r) => (
            <div key={r.review_id} className="rounded-xl bg-slate-50 px-3 py-2 text-[11px] text-slate-500" data-testid={`ar-row-${r.review_id}`}>
              {r.at.slice(0, 10)} · {r.scope} · {r.accounts_reviewed} accounts · {r.revocations} revocations · by {r.reviewer} · next due {r.next_due?.slice(0, 10)}
            </div>
          ))}
          {!reviews.length && <p className="text-[11px] text-slate-300">No access reviews recorded yet.</p>}
        </div>
      </div>
    </div>
  );
};

export const PolicyLifecyclePanel = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/compliance/policy-lifecycle").then(({ data }) => setD(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner />;
  const act = async (pid, action) => {
    try { await api.post(`/compliance/policies/${pid}/transition`, { action }); toast.success(`Policy ${action} — audited + evidenced`); load(); }
    catch (e) { toast.error(err(e)); }
  };
  const STATUS_TONE = { Published: "bg-emerald-50 text-emerald-600", Approved: "bg-cobalt/10 text-cobalt", "Pending Approval": "bg-amber-50 text-amber-600" };
  return (
    <div className="space-y-4" data-testid="cc-policy-lifecycle">
      <div className="grid grid-cols-4 gap-2">
        {Object.entries(d.counts).map(([k, v]) => (
          <div key={k} className="rounded-2xl bg-white border border-slate-100 p-3 text-center" data-testid={`pol-count-${k}`}>
            <div className="font-heading font-extrabold text-navy text-lg">{v}</div>
            <div className="text-[9px] uppercase font-bold text-slate-400">{k}</div>
          </div>
        ))}
      </div>
      <div className="rounded-3xl bg-white border border-slate-100 p-5">
        <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><BookOpenCheck className="w-4 h-4 text-cobalt" /> Policy lifecycle — Draft → Pending Approval → Approved → Published → acknowledged</h3>
        <div className="mt-3 space-y-1.5 max-h-[520px] overflow-y-auto">
          {d.policies.map((p) => (
            <div key={p.policy_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-100 px-3 py-2 text-xs" data-testid={`pol-${p.policy_id}`}>
              <div>
                <b className="text-navy">{p.name}</b> <span className="text-slate-300">v{p.version}</span>
                <div className="text-slate-400 text-[10px]">{p.approved_by ? `approved by ${p.approved_by}` : "not approved"} · acks (current v): {p.acknowledgments_current_version}/{p.eligible_users}{p.next_review_due ? ` · review due ${p.next_review_due.slice(0, 10)}` : ""}</div>
              </div>
              <div className="flex gap-1.5 items-center">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_TONE[p.status] || "bg-slate-100 text-slate-500"}`}>{p.status}</span>
                {(p.status || "").startsWith("Draft") && <button onClick={() => act(p.policy_id, "submit")} data-testid={`pol-submit-${p.policy_id}`} className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold">Submit</button>}
                {p.status === "Pending Approval" && <button onClick={() => act(p.policy_id, "approve")} data-testid={`pol-approve-${p.policy_id}`} className="px-2.5 py-1 rounded-full bg-cobalt text-white text-[10px] font-bold">Approve</button>}
                {p.status === "Approved" && <button onClick={() => act(p.policy_id, "publish")} data-testid={`pol-publish-${p.policy_id}`} className="px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold">Publish</button>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const TrainingPanel = () => {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ course_id: "", due_days: "30" });
  const load = useCallback(() => api.get("/compliance/training").then(({ data }) => setD(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner />;
  const assign = async () => {
    if (!form.course_id) { toast.error("Select a course"); return; }
    try {
      const { data: r } = await api.post("/compliance/training/assign", { course_id: form.course_id, due_days: Number(form.due_days) });
      toast.success(`${r.assignments_created} assignment(s) created for "${r.course}" (role-based)`); load();
    } catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="space-y-4" data-testid="cc-training">
      <div className="rounded-3xl bg-white border border-slate-100 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><GraduationCap className="w-4 h-4 text-cobalt" /> Security awareness training</h3>
          <div className="flex gap-2 items-center">
            <select value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value })} data-testid="trn-course" className={inputCls}>
              <option value="">Select course…</option>
              {d.courses.map((c) => <option key={c.course_id} value={c.course_id}>{c.name}</option>)}
            </select>
            <input type="number" value={form.due_days} onChange={(e) => setForm({ ...form, due_days: e.target.value })} data-testid="trn-due" className={`${inputCls} w-24`} />
            <button onClick={assign} data-testid="trn-assign" className="px-4 py-2 rounded-full bg-navy text-white text-xs font-bold">Assign to required roles</button>
          </div>
        </div>
        <div className="mt-3 grid md:grid-cols-2 gap-2">
          {d.courses.map((c) => (
            <div key={c.course_id} className="rounded-xl border border-slate-100 p-3 text-xs" data-testid={`trn-${c.course_id}`}>
              <b className="text-navy">{c.name}</b>
              <div className="text-slate-400 text-[10px] mt-0.5">roles: {c.required_roles.join(", ")} · refresher every {c.refresher_months} months</div>
              <div className="mt-1 flex gap-3">
                <span className="text-slate-500">assigned: <b>{c.assigned}</b></span>
                <span className="text-emerald-600">completed: <b>{c.completed}</b> ({c.completion_pct}%)</span>
                <span className={c.overdue ? "text-red-500" : "text-slate-400"}>overdue: <b>{c.overdue}</b></span>
              </div>
            </div>
          ))}
        </div>
        <h4 className="font-heading font-semibold text-navy text-xs mt-4">Recent assignments</h4>
        <div className="mt-1.5 space-y-1 max-h-48 overflow-y-auto">
          {d.recent_assignments.map((a) => (
            <div key={a.assignment_id} className="flex justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-[11px] text-slate-500">
              <span>{a.user_name} · {a.course_name}</span>
              <span className={a.status === "completed" ? "text-emerald-600 font-bold" : "text-slate-400"}>{a.status}{a.status === "assigned" ? ` · due ${a.due?.slice(0, 10)}` : ""}</span>
            </div>
          ))}
          {!d.recent_assignments.length && <p className="text-[11px] text-slate-300">No assignments yet — completions auto-generate CC2.1 evidence.</p>}
        </div>
      </div>
    </div>
  );
};
